import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Redirect, Route, Switch } from "wouter";
import { Suspense, lazy, useEffect } from "react";
import { EnhancedErrorBoundary } from "./components/EnhancedErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleGuard } from "./components/RoleGuard";
import { LoadingFallback } from "./components/LoadingFallback";
import { PageTransition } from "./components/PageTransition";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { GlobalKeyboardShortcuts } from "@/components/GlobalKeyboardShortcuts";
import { PushNotificationPrompt } from "@/components/PushNotificationPrompt";
import { initializeOneSignal, getPlayerId } from "@/lib/onesignal";
import { trpc } from "@/lib/trpc";
import { SkipNavigation } from "@/components/SkipNavigation";
import { NotificationToast } from "@/components/NotificationToast";
import { MobileScrollTracePanel } from "@/components/MobileScrollTracePanel";

// Lazy load pages for better performance
const NotFound = lazy(() => import("@/pages/NotFound"));
const LandPriceReference = lazy(() => import("@/pages/LandPriceReference"));
const GlobalCollateralMapPage = lazy(() => import("@/pages/GlobalCollateralMapPage"));
const LoginNew = lazy(() => import("@/pages/LoginNew"));
const ChangePasswordRequired = lazy(() => import("@/pages/ChangePasswordRequired"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
// Lazy load all other pages
const Reports = lazy(() => import("@/pages/Reports"));
const ReportBuilder = lazy(() => import("@/pages/ReportBuilder"));
const Customers = lazy(() => import("@/pages/Customers"));
const CAFForm = lazy(() => import("@/pages/CAFForm"));
const IAFForm = lazy(() => import("@/pages/IAFForm"));
const CTFForm = lazy(() => import("@/pages/CTFForm"));
const LoanCalculator = lazy(() => import("@/pages/LoanCalculator"));
const SalesAssistant = lazy(() => import("@/pages/SalesAssistant"));
const Tracking = lazy(() => import("@/pages/Tracking"));
const AddCustomerMobile = lazy(() => import("@/pages/AddCustomerMobile"));
const CustomersList = lazy(() => import("@/pages/CustomersList"));
const DashboardNew = lazy(() => import("@/pages/DashboardNew"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const LoanApplicationsList = lazy(() => import("@/pages/LoanApplicationsList"));
const MyApplications = lazy(() => import("@/pages/MyApplications"));
const MyApplicationsNew = lazy(() => import("@/pages/MyApplicationsNew"));
const LoanApplicationDetail = lazy(() => import("@/pages/LoanApplicationDetail"));
const StatusTrackingDetail = lazy(() => import("@/pages/StatusTrackingDetail"));
const CollateralDetail = lazy(() => import("@/pages/CollateralDetail"));
const EditCollateral = lazy(() => import("@/pages/EditCollateral"));
const AddCollateral = lazy(() => import("@/pages/AddCollateral"));
const NotificationSettingsOld = lazy(() => import("@/pages/NotificationSettings"));
const NotificationSettingsNew = lazy(() => import("@/pages/settings/NotificationSettings"));
const NotificationTesting = lazy(() => import("@/pages/settings/NotificationTesting"));
const NotificationTemplates = lazy(() => import("@/pages/settings/NotificationTemplates"));
const Testing = lazy(() => import("@/pages/Testing"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const ApprovalHistory = lazy(() => import("@/pages/ApprovalHistory"));
const AssignmentHistory = lazy(() => import("@/pages/AssignmentHistory"));
const SummaryReport = lazy(() => import("@/pages/SummaryReport"));
const BranchesManagement = lazy(() => import("@/pages/settings/BranchesManagement"));
const LocationCatalogueManagement = lazy(() => import("@/pages/settings/LocationCatalogueManagement"));
const DepartmentsManagement = lazy(() => import("@/pages/settings/DepartmentsManagement"));
const DepartmentsManagementNew = lazy(() => import("@/pages/settings/DepartmentsManagementNew"));
const CreditProductsManagement = lazy(() => import("@/pages/settings/CreditProductsManagement"));
const CreditProductTypesManagement = lazy(() => import("@/pages/settings/CreditProductTypesManagement"));
const DocumentUpload = lazy(() => import("@/pages/DocumentUpload"));
const SystemSettings = lazy(() => import("@/pages/settings/SystemSettings"));
const LoginAttemptsMonitor = lazy(() => import("@/pages/settings/LoginAttemptsMonitor"));
const SecurityAuditLogPage = lazy(() => import("@/pages/SecurityAuditLogPage"));
const AIKnowledgeSettings = lazy(() => import("@/pages/settings/AIKnowledgeSettings"));
const AssessmentCriteria = lazy(() => import("@/pages/settings/AssessmentCriteria"));
const GoogleDriveBackupSettings = lazy(() => import("@/pages/admin/GoogleDriveBackupSettings"));
const Holidays = lazy(() => import("@/pages/settings/Holidays"));
const AlertRules = lazy(() => import("@/pages/settings/AlertRules"));
const ScheduledExports = lazy(() => import("@/pages/settings/ScheduledExports"));
const SmartAlerts = lazy(() => import("@/pages/settings/SmartAlerts"));
const SlaSettings = lazy(() => import("@/pages/settings/SlaSettings"));
const FinancialCategories = lazy(() => import("@/pages/settings/FinancialCategories"));
const WorkflowCheckpointCatalogueSettings = lazy(() => import("@/pages/settings/WorkflowCheckpointCatalogueSettings"));
const EmailSettings = lazy(() => import("@/pages/settings/EmailSettings"));
const QuickActionsSettings = lazy(() => import("@/pages/settings/QuickActionsSettings"));
const ServerCacheSettings = lazy(() => import("@/pages/settings/ServerCacheSettings"));
const DataManagement = lazy(() => import("@/pages/settings/DataManagement"));
const WorkflowConfig = lazy(() => import("@/pages/settings/WorkflowConfig"));
const CollateralMapSettings = lazy(() => import("@/pages/settings/CollateralMapSettings"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ErrorLogs = lazy(() => import("@/pages/ErrorLogs"));
const PerformanceDashboard = lazy(() => import("@/pages/PerformanceDashboard"));
const MonitoringDashboard = lazy(() => import("@/pages/MonitoringDashboard"));
const ExportHistory = lazy(() => import("@/pages/ExportHistory"));
const AdvancedSearch = lazy(() => import("@/pages/AdvancedSearch"));
const GlobalSearch = lazy(() => import("@/pages/GlobalSearch"));
const SearchAnalyticsDashboard = lazy(() => import("@/pages/SearchAnalyticsDashboard"));
const DashboardWidgets = lazy(() => import("@/pages/DashboardWidgets"));
const ReportTemplates = lazy(() => import("@/pages/ReportTemplates"));
const ReportSchedules = lazy(() => import("@/pages/ReportSchedules"));
const EmailSchedules = lazy(() => import("@/pages/EmailSchedules"));
const EmailHistory = lazy(() => import("@/pages/EmailHistory"));
const NotificationHistory = lazy(() => import("@/pages/NotificationHistory"));
const NotificationPreferences = lazy(() => import("@/pages/NotificationPreferences"));
const SmtpSettings = lazy(() => import("@/pages/SmtpSettings"));
const GroupNotifications = lazy(() => import("@/pages/GroupNotifications"));
const NotificationAnalytics = lazy(() => import("@/pages/NotificationAnalytics"));
const EmailTemplatesManagement = lazy(() => import("@/pages/EmailTemplatesManagement"));
const ScheduledNotifications = lazy(() => import("@/pages/ScheduledNotifications"));
const NotificationRulesManagement = lazy(() => import("@/pages/NotificationRulesManagement"));
const Webhooks = lazy(() => import("@/pages/Webhooks"));
const WebhookLogs = lazy(() => import("@/pages/WebhookLogs"));
const WebhookTemplatesManagement = lazy(() => import("@/pages/WebhookTemplatesManagement"));
const WebhookDeliveryDashboard = lazy(() => import("@/pages/WebhookDeliveryDashboard"));
const NotificationPreferencesAdvanced = lazy(() => import("@/pages/NotificationPreferencesAdvanced"));
const NotificationHistoryExport = lazy(() => import("@/pages/NotificationHistoryExport"));
const Health = lazy(() => import("@/pages/Health"));
const SubmitFeedback = lazy(() => import("@/pages/SubmitFeedback"));
const FeedbackDashboard = lazy(() => import("@/pages/FeedbackDashboard"));
const Feedback = lazy(() => import("@/pages/Feedback"));
const FeedbackManagement = lazy(() => import("@/pages/FeedbackManagement"));
const AdminFeedbackManagement = lazy(() => import("@/pages/admin/FeedbackManagement"));
const WebhookStats = lazy(() => import("@/pages/WebhookStats"));
const WebhookTemplates = lazy(() => import("@/pages/WebhookTemplates"));
const ReportExecutionLogs = lazy(() => import("@/pages/ReportExecutionLogs"));
const WebhookRetryQueue = lazy(() => import("@/pages/WebhookRetryQueue"));
const WebhookSecuritySettings = lazy(() => import("@/pages/WebhookSecuritySettings"));
const WebhookPerformanceMonitoring = lazy(() => import("@/pages/WebhookPerformanceMonitoring"));
const WebhookHealthDashboard = lazy(() => import("@/pages/WebhookHealthDashboard"));
const WebhookAlertRules = lazy(() => import("@/pages/WebhookAlertRules"));
const WebhookPerformanceTrends = lazy(() => import("@/pages/WebhookPerformanceTrends"));
const WebhookTestingTool = lazy(() => import("@/pages/WebhookTestingTool"));
const WebhookDashboard = lazy(() => import("@/pages/WebhookDashboard"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));
const ProfileSettings = lazy(() => import("@/pages/ProfileSettings"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const WorkflowAnalytics = lazy(() => import("@/pages/WorkflowAnalytics"));
const WorkflowTemplatesPage = lazy(() => import("@/pages/WorkflowTemplates"));
const BranchApprovalLimits = lazy(() => import("@/pages/settings/BranchApprovalLimits"));
const ExchangeRateSettings = lazy(() => import("@/pages/settings/ExchangeRateSettings")); // Sprint v223
const DocumentSettings = lazy(() => import("@/pages/settings/DocumentSettings"));
const DocumentRequirements = lazy(() => import("@/pages/settings/DocumentRequirements"));
const DocumentCategories = lazy(() => import("@/pages/settings/DocumentCategories"));
const ContractTemplateStudioSettings = lazy(() => import("@/pages/settings/ContractTemplateStudioSettings"));
const DecisionAuditLogs = lazy(() => import("@/pages/DecisionAuditLogs"));
const HODashboard = lazy(() => import("@/pages/HODashboard"));
const CodeReview = lazy(() => import("@/pages/CodeReview"));
const SharedDocumentView = lazy(() => import("@/pages/SharedDocumentView"));
const SharedCalculationView = lazy(() => import("@/pages/SharedCalculationView"));
const PushNotificationSetup = lazy(() => import("@/pages/PushNotificationSetup"));
const AccessReport = lazy(() => import("@/pages/AccessReport"));
const EditCustomerInfo = lazy(() => import("@/pages/EditCustomerInfo"));
const NewLoanForm = lazy(() => import("@/pages/NewLoanForm"));
const CTFChecklistSettings = lazy(() => import("@/pages/settings/CTFChecklistSettings"));

// BottomNavigationWrapper ຖືກລຶບອອກ - ໃຊ້ MobileBottomNav ໃນ DashboardLayout ແທນ

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      {/* Public routes - no authentication required */}
      <Route path="/share/:shareId" component={SharedDocumentView} />
      <Route path="/shared/:token" component={SharedCalculationView} />
      <Route path="/push-setup" component={PushNotificationSetup} />
      <Route path="/login" component={LoginNew} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route
        path="/change-password-required"
        component={ChangePasswordRequired}
      />
      <Route path="/dashboard">
        {() => (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/dashboard-new">
        {() => (
          <ProtectedRoute>
            <DashboardNew />
          </ProtectedRoute>
        )}
      </Route>
      {/* <Route path="/customer/new" component={CustomerForm} /> */}{" "}
      {/* ລຶບແລ້ວ - ໃຊ້ /add-customer ແທນ */}
      <Route path="/add-customer">
        {() => (
          <ProtectedRoute>
            <AddCustomerMobile mode="add" />
          </ProtectedRoute>
        )}
      </Route>
      {/* V474: legacy split-write route is retained as a safe deep-link redirect only. */}
      <Route path="/add-customer-old">
        {() => (
          <ProtectedRoute>
            <Redirect to="/add-customer" />
          </ProtectedRoute>
        )}
      </Route>
      <Route<{ id: string }> path="/edit-customer/:id">
        {params => {
          const id = params.id ? parseInt(params.id) : undefined;
          return (
            <ProtectedRoute>
              <EditCustomerInfo />
            </ProtectedRoute>
          );
        }}
      </Route>
      {/* ໜ້າແກ້ໄຂຮ່າງ 7 tabs ສຳລັບ service_staff (draft status) */}
      <Route<{ id: string }> path="/edit-customer-mobile/:id">
        {params => {
          const id = params.id ? parseInt(params.id) : undefined;
          return (
            <ProtectedRoute>
              <AddCustomerMobile mode="edit" customerId={id} />
            </ProtectedRoute>
          );
        }}
      </Route>
      <Route path="/new-loan/:customerId">
        {() => (
          <ProtectedRoute>
            <NewLoanForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/add-customer-complete">
        {() => (
          <ProtectedRoute>
            <Redirect to="/add-customer" />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/customers-list">
        {() => (
          <ProtectedRoute>
            <CustomersList />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/customer/:id">
        {() => (
          <ProtectedRoute>
            <CustomerDetail />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/search">
        {() => (
          <ProtectedRoute>
            <GlobalSearch />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/search-analytics">
        {() => (
          <ProtectedRoute>
            <SearchAnalyticsDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-applications">
        {() => (
          <ProtectedRoute>
            <LoanApplicationsList />
          </ProtectedRoute>
        )}
      </Route>
      {/* My Applications - Role-based filtered view */}
      <Route path="/my-applications">
        {() => (
          <ProtectedRoute>
            <MyApplicationsNew />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/status-tracking/:id">
        {() => (
          <ProtectedRoute>
            <StatusTrackingDetail />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:id">
        {() => (
          <ProtectedRoute>
            <LoanApplicationDetail />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-applications/:id">
        {() => (
          <ProtectedRoute>
            <LoanApplicationDetail />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notification-settings">
        {() => (
          <ProtectedRoute>
            <NotificationSettingsOld />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notifications">
        {() => (
          <ProtectedRoute>
            <NotificationHistory />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notification-preferences">
        {() => (
          <ProtectedRoute>
            <NotificationPreferences />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/testing">
        {() => (
          <ProtectedRoute>
            <Testing />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/code-review">
        {() => (
          <ProtectedRoute>
            <CodeReview />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/users">
        {() => (
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/access-report">
        {() => (
          <ProtectedRoute>
            <AccessReport />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/activity-log">
        {() => (
          <ProtectedRoute>
            <ActivityLog />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/approval-history">
        {() => (
          <ProtectedRoute>
            <ApprovalHistory />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/activity-logs">
        {() => (
          <ProtectedRoute>
            <ActivityLog />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/assignment-history">
        {() => (
          <ProtectedRoute>
            <AssignmentHistory />
          </ProtectedRoute>
        )}
      </Route>
      {/* <Route path="/notifications" component={NotificationsList} /> */}{" "}
      {/* Removed temporarily */}
      <Route path="/customers">
        {() => (
          <ProtectedRoute>
            <Customers />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/reports">
        {() => (
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/reports/builder">
        {() => (
          <ProtectedRoute>
            <ReportBuilder />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/summary-report">
        {() => (
          <ProtectedRoute>
            <SummaryReport />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/report-templates">
        {() => (
          <ProtectedRoute>
            <ReportTemplates />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/report-schedules">
        {() => (
          <ProtectedRoute>
            <ReportSchedules />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/forms/caf">
        {() => (
          <ProtectedRoute>
            <CAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/iaf-form/:id">
        {() => (
          <ProtectedRoute>
            <IAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/forms/iaf">
        {() => (
          <ProtectedRoute>
            <IAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/forms/iaf/:id">
        {() => (
          <ProtectedRoute>
            <IAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/forms/ctf">
        {() => (
          <ProtectedRoute>
            <CTFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/forms/ctf/:id">
        {() => (
          <ProtectedRoute>
            <CTFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:id/caf">
        {() => (
          <ProtectedRoute>
            <CAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:id/iaf">
        {() => (
          <ProtectedRoute>
            <IAFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:id/ctf">
        {() => (
          <ProtectedRoute>
            <CTFForm />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:id/add-collateral">
        {() => (
          <ProtectedRoute>
            <AddCollateral />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:loanId/collateral/:collateralId/edit">
        {() => (
          <ProtectedRoute>
            <EditCollateral />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-application/:loanId/collateral/:collateralId">
        {() => (
          <ProtectedRoute>
            <CollateralDetail />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/loan-calculator">
        {() => (
          <ProtectedRoute>
            <LoanCalculator />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/sales-assistant">
        {() => (
          <ProtectedRoute>
            <SalesAssistant />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/documents/upload">
        {() => (
          <ProtectedRoute>
            <DocumentUpload />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/tracking">
        {() => (
          <ProtectedRoute>
            <Tracking />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/advanced-search">
        {() => (
          <ProtectedRoute>
            <AdvancedSearch />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/dashboard-widgets">
        {() => (
          <ProtectedRoute>
            <DashboardWidgets />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/branches">
        {() => (
          <ProtectedRoute>
            <BranchesManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/departments">
        {() => (
          <ProtectedRoute>
            <DepartmentsManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/departments-management">
        {() => (
          <ProtectedRoute>
            <DepartmentsManagementNew />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/credit-products">
        {() => (
          <ProtectedRoute>
            <CreditProductsManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/product-types">
        {() => (
          <ProtectedRoute>
            <CreditProductTypesManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/system">
        {() => (
          <ProtectedRoute>
            <SystemSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/login-monitor">
        {() => (
          <ProtectedRoute>
            <LoginAttemptsMonitor />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/security-audit-log">
        {() => (
          <ProtectedRoute>
            <SecurityAuditLogPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/ai-knowledge">
        {() => (
          <ProtectedRoute>
            <AIKnowledgeSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/ctf-checklist">
        {() => (
          <ProtectedRoute>
            <CTFChecklistSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/assessment-criteria">
        {() => (
          <ProtectedRoute>
            <AssessmentCriteria />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/financial-categories">
        {() => (
          <ProtectedRoute>
            <FinancialCategories />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/workflow-checkpoint-catalogue">
        {() => (
          <ProtectedRoute>
            <WorkflowCheckpointCatalogueSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/google-drive-backup">
        {() => (
          <ProtectedRoute>
            <GoogleDriveBackupSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/smtp">
        {() => (
          <ProtectedRoute>
            <SmtpSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notifications/group">
        {() => (
          <ProtectedRoute>
            <GroupNotifications />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhooks">
        {() => (
          <ProtectedRoute>
            <Webhooks />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-logs">
        {() => (
          <ProtectedRoute>
            <WebhookLogs />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-templates">
        {() => (
          <ProtectedRoute>
            <WebhookTemplatesManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-retry-queue">
        {() => (
          <ProtectedRoute>
            <WebhookRetryQueue />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-security">
        {() => (
          <ProtectedRoute>
            <WebhookSecuritySettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-performance">
        {() => (
          <ProtectedRoute>
            <WebhookPerformanceMonitoring />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/webhook-health">
        {() => (
          <ProtectedRoute>
            <WebhookHealthDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-alert-rules">
        {() => (
          <ProtectedRoute>
            <WebhookAlertRules />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-performance-trends">
        {() => (
          <ProtectedRoute>
            <WebhookPerformanceTrends />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/webhook-testing-tool">
        {() => (
          <ProtectedRoute>
            <WebhookTestingTool />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/webhook-delivery-dashboard">
        {() => (
          <ProtectedRoute>
            <WebhookDeliveryDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/webhook-dashboard">
        {() => (
          <ProtectedRoute>
            <WebhookDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/notifications">
        {() => (
          <ProtectedRoute>
            <NotificationSettingsNew />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/notification-testing">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <NotificationTesting />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/notification-templates">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <NotificationTemplates />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/notification-preferences-advanced">
        {() => (
          <ProtectedRoute>
            <NotificationPreferencesAdvanced />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notifications/history-export">
        {() => (
          <ProtectedRoute>
            <NotificationHistoryExport />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/notification-rules">
        {() => (
          <ProtectedRoute>
            <NotificationRulesManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/scheduled-notifications">
        {() => (
          <ProtectedRoute>
            <ScheduledNotifications />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/email-templates">
        {() => (
          <ProtectedRoute>
            <EmailTemplatesManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/notifications/analytics">
        {() => (
          <ProtectedRoute>
            <NotificationAnalytics />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/holidays">
        {() => (
          <ProtectedRoute>
            <Holidays />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/location-catalogue">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <LocationCatalogueManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/alert-rules">
        {() => (
          <ProtectedRoute>
            <AlertRules />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/scheduled-exports">
        {() => (
          <ProtectedRoute>
            <ScheduledExports />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/export-history">
        {() => (
          <ProtectedRoute>
            <ExportHistory />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/smart-alerts">
        {() => (
          <ProtectedRoute>
            <SmartAlerts />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/email-schedules">
        {() => (
          <ProtectedRoute>
            <EmailSchedules />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/email-history">
        {() => (
          <ProtectedRoute>
            <EmailHistory />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/sla">
        {() => (
          <ProtectedRoute>
            <SlaSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/email">
        {() => (
          <ProtectedRoute>
            <EmailSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/quick-actions">
        {() => (
          <ProtectedRoute>
            <QuickActionsSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/server-cache">
        {() => (
          <ProtectedRoute>
            <ServerCacheSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/data-management">
        {() => (
          <ProtectedRoute>
            <DataManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/workflow-config">
        {() => (
          <ProtectedRoute>
            <WorkflowConfig />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/collateral-map-settings">
        {() => (
          <ProtectedRoute>
            <CollateralMapSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/workflow-analytics">
        {() => (
          <ProtectedRoute>
            <WorkflowAnalytics />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/workflow-templates">
        {() => (
          <ProtectedRoute>
            <WorkflowTemplatesPage />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/error-logs">
        {() => (
          <ProtectedRoute>
            <ErrorLogs />
          </ProtectedRoute>
        )}
      </Route>{" "}
      <Route path="/performance-dashboard">
        {() => (
          <ProtectedRoute>
            <PerformanceDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/monitoring-dashboard">
        {() => (
          <ProtectedRoute>
            <MonitoringDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/performance">
        {() => (
          <ProtectedRoute>
            <PerformanceDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/health">
        {() => (
          <ProtectedRoute>
            <Health />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/feedback/submit">
        {() => (
          <ProtectedRoute>
            <SubmitFeedback />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/feedback/dashboard">
        {() => (
          <ProtectedRoute>
            <FeedbackDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/feedback">
        {() => (
          <ProtectedRoute>
            <Feedback />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/feedback">
        {() => (
          <ProtectedRoute>
            <AdminFeedbackManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/feedback-management">
        {() => (
          <ProtectedRoute>
            <FeedbackManagement />
          </ProtectedRoute>
        )}
      </Route>
      <Route<{ id: string }> path="/feedbacks/:id">
        {params => {
          const id = params.id ? parseInt(params.id) : undefined;
          return (
            <ProtectedRoute>
              <FeedbackManagement feedbackId={id} />
            </ProtectedRoute>
          );
        }}
      </Route>
      <Route path="/admin/webhook-stats">
        {() => (
          <ProtectedRoute>
            <WebhookStats />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/webhook-templates">
        {() => (
          <ProtectedRoute>
            <WebhookTemplates />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/report-logs">
        {() => (
          <ProtectedRoute>
            <ReportExecutionLogs />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/analytics">
        {(params: any) => (
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/profile">
        {(params: any) => (
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/profile/settings">
        {(params: any) => (
          <ProtectedRoute>
            <ProfileSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/profile/change-password">
        {(params: any) => (
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/branch-approval-limits">
        {() => (
          <ProtectedRoute>
            <BranchApprovalLimits />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/exchange-rates">
        {() => (
          <ProtectedRoute>
            <ExchangeRateSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/documents">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <DocumentSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/document-requirements">
        {() => (
          <ProtectedRoute>
            <DocumentRequirements />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/document-categories">
        {() => (
          <ProtectedRoute>
            <DocumentCategories />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings/contract-template-studio">
        {() => (
          <ProtectedRoute>
            <ContractTemplateStudioSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/decision-audit-logs">
        {() => (
          <ProtectedRoute>
            <DecisionAuditLogs />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/ho-dashboard">
        {() => (
          <ProtectedRoute>
            <HODashboard />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/land-price">
        {() => (
          <ProtectedRoute>
            <LandPriceReference />
          </ProtectedRoute>
        )}
      </Route>
      {/* Sprint v164 — Global Collateral Map Page */}
      <Route path="/collateral-map">
        {() => (
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <GlobalCollateralMapPage />
            </Suspense>
          </ProtectedRoute>
        )}
      </Route>
      {/* Sprint v76 — UnifiedDrawSpike isolated prototype (no auth required for device testing) */}
      <Route path="/spike/unified-draw">
        {() => {
          const UnifiedDrawSpike = lazy(() => import("@/pages/UnifiedDrawSpike"));
          return <Suspense fallback={<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#111',color:'#fff'}}>Loading spike...</div>}><UnifiedDrawSpike /></Suspense>;
        }}
      </Route>
      <Route path="/404" component={NotFound} />
      {/* Root path - must be at the end before fallback */}
      <Route path="/">
        {() => (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        )}
      </Route>
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  // Initialize OneSignal on app mount
  useEffect(() => {
    initializeOneSignal();
  }, []);

  // Auto-save Player ID when user is logged in and OneSignal is ready
  const savePlayerIdMutation = trpc.onesignal.savePlayerId.useMutation();
  
  useEffect(() => {
    const savePlayerIdIfNeeded = async () => {
      try {
        console.log('[Player ID Auto-Save] Starting auto-save process...');
        
        const userStr = localStorage.getItem('user');
        console.log('[Player ID Auto-Save] User from localStorage:', userStr ? 'Found' : 'Not found');
        
        if (!userStr) {
          console.log('[Player ID Auto-Save] No user in localStorage, skipping');
          return;
        }

        const user = JSON.parse(userStr);
        console.log('[Player ID Auto-Save] User ID:', user.id);
        
        const playerId = await getPlayerId();
        console.log('[Player ID Auto-Save] Player ID from OneSignal:', playerId || 'Not available yet');
        
        if (playerId && user.id) {
          // Check if Player ID is already saved in localStorage to avoid duplicate saves
          const savedPlayerId = localStorage.getItem('onesignal_player_id');
          console.log('[Player ID Auto-Save] Previously saved Player ID:', savedPlayerId || 'None');
          
          if (savedPlayerId === playerId) {
            console.log('[Player ID Auto-Save] Player ID already saved, skipping');
            return;
          }

          console.log('[Player ID Auto-Save] Sending Player ID to server via tRPC...');
          
          // Save to database via tRPC
          try {
            const result = await savePlayerIdMutation.mutateAsync({ playerId });
            console.log('[Player ID Auto-Save] tRPC response:', result);
            localStorage.setItem('onesignal_player_id', playerId);
            console.log('✅ [Player ID Auto-Save] Successfully saved Player ID:', playerId);
          } catch (error) {
            console.error('❌ [Player ID Auto-Save] Failed to save Player ID to database:', error);
          }
        } else {
          console.log('[Player ID Auto-Save] Missing playerId or user.id:', { playerId, userId: user?.id });
        }
      } catch (error) {
        console.error('❌ [Player ID Auto-Save] Failed to auto-save OneSignal Player ID:', error);
      }
    };

    // Wait for OneSignal to initialize before saving
    console.log('[Player ID Auto-Save] Scheduling auto-save in 3 seconds...');
    const timer = setTimeout(savePlayerIdIfNeeded, 3000);
    return () => clearTimeout(timer);
  }, [savePlayerIdMutation]);

  return (
    <EnhancedErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        switchable
      >
        <AuthProvider>
          <WebSocketProvider>
            <BrandingProvider>
            <TooltipProvider>
              <SkipNavigation />
              <Toaster />
              <NotificationToast />
              <GlobalKeyboardShortcuts />
              <OfflineIndicator />
              <PushNotificationPrompt />
              <MobileScrollTracePanel />
              <Suspense fallback={<LoadingFallback />}>
                <PageTransition>
                  <RoleGuard>
                    <Router />
                  </RoleGuard>
                </PageTransition>
              </Suspense>
              {/* BottomNavigation ຖືກຈັດການໂດຍ DashboardLayout/DashboardLayoutMobile ແລ້ວ */}
            </TooltipProvider>
            </BrandingProvider>
          </WebSocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </EnhancedErrorBoundary>
  );
}

export default App;
